<template>
  <div class="gameCategory" v-if="category">
    <NavBar :left-text="title" leftArrow />

    <div class="p-4 flex-1 flex flex-col">
      <van-field v-model.lazy="searchValue" clearable autocomplete="off" autocapitalize="off" :right-icon="iSearch"/>

      <div class="flex pt-4 gap-3 flex-1">
        <div class="shrink-0" v-if="showSidebar">
          <div class="flex flex-col gap-3 overflow-y-scroll text-13 h-[calc(var(--vh)*100-18rem)]">
            <div
              v-for="item, i in roomList"
              ref="roomListRef"
              @click="changeRoom(i)"
              :class="['wh-[60px_56px] flex-center-col rounded-4 bg-[#FFFFFF0D] shrink-0', {
              'bg-gradient-to-b from-[#3B913B] to-[#024E02]': i == curRoomIndex
            }]">
              <Image v-if="item.customIcon || item.icon" :src="item.customIcon || item.icon" class="size-8 mt-1" alt=""/>
              <span>{{ item.display || item.customName || item.name }}</span>
            </div>
          </div>
        </div>

        <div class="flex flex-col flex-1 overflow-hidden gap-2 relative">
          <div class="flex-center-y overflow-x-auto gap-2 [&>:has(.logo)]:h-8" v-if="useUserInfo().isLoggedin">
            <div v-if="!showSidebar && icon" class="logo">
              <Image :src="icon" class="size-8 rounded-sm"/>
            </div>
            <span
              v-for="item, i in sortTypeFilters"
              :key="item.en_label"
              class="primaryBtn-thin px-2 !text-13 !rounded-4 !h-6"
              :class="sortIndex !== i && 'primaryBtn_transparent-thin !text-white/75'"
              @click="sortList(i)"
            >{{ item[language + '_label'] }}</span>
          </div>

          <div class="h-[calc(var(--vh)*100-18rem)] overflow-y-scroll">
            <GameLists v-if="gamelist.length" :list="gamelist" showFavorite class="gap-2.5 pb-8" :itemClass="showSidebar ? 'wh-[83px_112px]' : 'wh-[77px_105px]'"/>
            <div v-else class="flex-center-x pt-[30%]">
              <van-loading type="spinner" v-if="total < 0" size="35" />
              <NoData v-else-if="total == 0" type="data" />
            </div>
          </div>

          <van-pagination v-if="total > 0" class="absolute-[auto_0_0] flex-center-x bg-[var(--theme-primary-dark)] pt-2 relative z-[10]" v-model="curPage" force-ellipses :total-items="total" :items-per-page="30" show-page-size="4" >
            <template #prev-text>
              <van-icon name="arrow-left" />
            </template>
            <template #next-text>
              <van-icon name="arrow" />
            </template>
            <template #page="{ text }"> {{ text }} </template>
          </van-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { iGame, iGameCategory, iRoom } from '@/types/index'
import { useSystemInfo, useGameCategory, useUserInfo, useFavGamelist } from '@/store'
import NavBar from '@a/components/NavBar/index.vue'
import iSearch from '@a/assets/images/i_search.png'
import GameLists from '@a/components/GameLists/index.vue'
import debounce from 'lodash-es/debounce'
import blueimp from 'blueimp-md5';
import NoData from '@/components/NoData/index.vue'
import * as api from '@/api'

if (useUserInfo().isLoggedin) useFavGamelist().init()

const router = useRouter()
const route = useRoute()

const category = ref<iGameCategory | null>(null)

const isInternal = computed(() => category.value?.type === 'Internal')
const pageSize = computed(() => isInternal.value ? 30 : 32)

const { currentCategory } = history.state
if (currentCategory) {
  category.value = currentCategory
} else {
  const { cid } = route.query
  if (cid) {
    category.value = useGameCategory().list.find(item => item.id + '' === cid) || null
  } else {
    console.warn('Error: invalid cid')

    showToast({
      message: 'Error: invalid cid',
      duration: 3500,
      onClose: () => {
        router.push('/')
      }
    })
  }
}

const icon = category.value?.customIcon || category.value?.defaultIcon || category.value?.icon

const isCustom = category.value?.type === 'Custom'
const title = category.value?.customName || category.value?.name
const showSidebar = computed(() => !isCustom && category.value?.type !== 'Hot')

const searchValue = ref('')
const curPage = ref(1)
const sortIndex = ref(0)
const curRoomIndex = ref(0)
const pageTabs = ref<{[key: string]: {isLoading: boolean, data: iGame[], total: number}}>({})
const favGameList = ref<{isLoading: boolean, data: iGame[], total: number}>({isLoading: false, data: [], total: -1})
const token = computed(() => blueimp(`${sortIndex.value}_${curRoomIndex.value}_${curPage.value}_${searchValue.value}`))
const gameData = computed(() => sortIndex.value < 2 ? pageTabs.value[token.value] : favGameList.value)
const gamelist = computed(() => gameData.value?.data || [])
const total = computed(() => gameData.value?.total || -1)

const roomList = ref<Partial<iRoom>[]>([ ])
const getRoomList = async () => {
  const {code, data} = await api.getRoomList({
    name: category.value?.name,
    showType: 'All',
    scope: 'RoomGame',
    show: 1
  })

  if (code === 1) {
    roomList.value = data.map(item => item.info)
    roomList.value.unshift({
      id: category.value?.id || 0,
      name: category.value?.name || '',
      customIcon: category.value?.customIcon || category.value?.defaultIcon || '',
      icon: category.value?.icon || category.value?.defaultIcon || '',
      show: 1
    })
  }
}

const roomListRef = ref<HTMLDivElement[]>([])
const changeRoom = (i: number) => {
  if (i == curRoomIndex.value) return

  curRoomIndex.value = i
  curPage.value = 1
}

const language = useSystemInfo().language
const sortTypeFilters = [
    {
      en_label: 'all',
      pt_label: 'Todos',
    },
    {
      en_label: 'recently',
      pt_label: 'Recente',
    },
    {
      en_label: 'favour',
      pt_label: 'Favoritos',
    }
  ];

  const fns = () => {
    const params = {
      category: category.value?.name,
      room: curRoomIndex.value == 0 ? '' : roomList.value[curRoomIndex.value]?.name,
      gameName: searchValue.value,
      pageIndex: curPage.value - 1,
      pageSize: pageSize.value
    }

    if (category.value?.name === 'HOT') {
      delete params.category
      delete params.room
    }

    return {
      fav: (signal: AbortSignal) => api.getFavoriteGameList(params, {
        signal
      }),
      played: (signal: AbortSignal) => api.getPlayedGameList(params, {
        signal
      }),
      room: (signal: AbortSignal) => api.getGameListByCategory({
        ...params,
        show: 1,
        name: category.value?.name,
        scope: curRoomIndex.value == 0 ? 'AllGame' : 'RoomGame',
      }, {
        signal
      })
    }
  }

  const cancelTokenSource = ref<AbortController | null>(null)
  const updateList = (list) => {
    if (sortIndex.value == 2) {
      favGameList.value = list
    } else {
      pageTabs.value[token.value] = list
    }
  }
  const updateGameList = debounce(async (type: 'fav' | 'played' | 'room') => {
    const d = gameData.value
    if (d?.isLoading || (type != 'fav' && (d?.data.length || d?.total == 0))) return

    if (cancelTokenSource.value) {
      cancelTokenSource.value.abort()
    }

    cancelTokenSource.value = new AbortController()

    const list = {
      isLoading: true,
      data: [],
      total: -1
    }

    updateList(JSON.parse(JSON.stringify(list)))

    const queryFn = fns()[type]
    try {
      const { code, data } = await queryFn(cancelTokenSource.value.signal)
      if (code === 1 && data?.data) {
        list.data = data.data.map(item => item.info)
        list.total = data.total
      }
    } finally {
      cancelTokenSource.value = null
      list.isLoading = false

      updateList(list)
    }
  }, 300)

  const sortTypes = ['room', 'played', 'fav']
  const sortList = async (i: number) => {
    if (sortIndex.value == i) return
    sortIndex.value = i
    curPage.value = 1

    if (sortIndex.value == 2) {
      // 重置收藏列表
      favGameList.value = {
        isLoading: false,
        data: [],
        total: -1
      }
    }
  }

const init = async () => {
  if (category.value?.type === 'Custom') {
    const {relatedCategory, relatedRoom} = category.value

    if (relatedCategory) {
      category.value = useGameCategory().list.find(item => item.name === relatedCategory) || null
    }

    await getRoomList()

    if (relatedRoom) {
      const index = roomList.value.findIndex(item => item.name === relatedRoom) || 0
      changeRoom(index)
    }
  } else {
    await getRoomList()
  }

  watch(token, () => {
    updateGameList(sortTypes[sortIndex.value])
  }, {
    immediate: true
  })
}

init()
</script>

<style scoped lang="scss">
.gameCategory {
  --van-cell-background: var(--navbar-bg);

  --van-pagination-height: 24px;
  --van-pagination-item-width: 24px;
  --van-pagination-item-border-radius: 4px;
  --van-pagination-background: transparent;
  --van-pagination-item-disabled-background: transparent;
  --van-pagination-item-default-color: white;
  --input-active-bg-color: var(--input-focus-bg-color);

  @apply flex flex-col h-[calc(var(--vh,1vh)*100)];

  :deep(.van-hairline--surround){
    @apply rounded-4 bd-[1px_var(--theme-primary-gray)];

    &::after{
      @apply hidden;
    }
  }

  :deep(.van-pagination__item--active){
    @apply bg-[var(--input-active-bg-color)] border-none;
  }

  :deep(.van-pagination__items){
    @apply flex gap-[8px];
  }

  :deep(.van-field) {
    @apply rounded-full;

    // &::after {
    //   @apply hidden;
    // }
  }
}
</style>
